import type { FleetMachine } from "@/context/fleet"
import { ServerUnavailableError } from "@/transport/server"
import { jitterDelay } from "@/utils/network-schedule"
import { transientMachineAccessFailure } from "@/transport/fleet"
import { abortable } from "@/utils/abortable"

/** How long a failed renewal is reused before the next attempt: a denial the whole time, a network failure 1s and doubling. */
const ACCESS_RETRY_MAX_MS = 15_000
const accessRetryDelay = (failures: number, denied: boolean) =>
  denied ? ACCESS_RETRY_MAX_MS : Math.min(1000 * 2 ** (failures - 1), ACCESS_RETRY_MAX_MS)

export function machineAccessRefreshDelay(machine: FleetMachine, now = Date.now(), random = Math.random) {
  const delay = machine.accessError ? ACCESS_RETRY_MAX_MS
    : Math.max(10_000, (machine.accessTokenExpiresAt ?? now / 1000) * 1000 - now - 120_000)
  return jitterDelay(delay, Math.min(15_000, Math.max(1000, Math.floor(delay * 0.1))), random)
}

export function createMachineAccessController(options: {
  read: (name: string) => FleetMachine | undefined
  refresh: (machine: FleetMachine, signal: AbortSignal) => Promise<FleetMachine>
  update: (machine: FleetMachine) => void
  failed: (name: string, error: unknown, usable: boolean) => void
}) {
  const pending = new Map<string, Promise<FleetMachine>>()
  const lifetime = new AbortController()
  const rejected = new Map<string, string>()
  const failures = new Map<string, { error: unknown; retryAt: number; denied: boolean; count: number }>()
  let disposed = false
  // Machines kept renewed ahead of expiry: the ones on screen.
  let kept: readonly string[] = []
  let visible = false
  const timers = new Map<string, { timer: ReturnType<typeof setTimeout>; at: number }>()

  const clearTimer = (name: string) => {
    const scheduled = timers.get(name)
    if (scheduled) clearTimeout(scheduled.timer)
    timers.delete(name)
  }

  const schedule = (name: string) => {
    if (disposed || !visible || !kept.includes(name)) return
    const machine = options.read(name)
    if (!machine || pending.has(name)) {
      clearTimer(name)
      return
    }
    const delay = machineAccessRefreshDelay(machine)
    const next = Date.now() + delay
    // Heartbeats must not keep postponing an already scheduled retry.
    const scheduled = timers.get(name)
    if (scheduled && scheduled.at <= next) return
    clearTimer(name)
    timers.set(name, {
      at: next,
      timer: setTimeout(() => {
        timers.delete(name)
        void refresh(name)
      }, delay),
    })
  }

  const ensure = async (name: string, rejectedToken?: string): Promise<FleetMachine | undefined> => {
    if (disposed) throw new ServerUnavailableError()
    const machine = options.read(name)
    if (!machine) return undefined
    if (rejectedToken && rejectedToken === machine.accessToken) rejected.set(name, rejectedToken)
    const usable = (value: FleetMachine) => !!value.accessToken && value.accessToken !== rejected.get(name) &&
      (value.accessTokenExpiresAt ?? 0) * 1000 > Date.now() && !failures.get(name)?.denied
    const valid = usable(machine)
    const existing = pending.get(name)
    if (existing) return valid ? machine : existing
    const failure = failures.get(name)
    if (failure && failure.retryAt > Date.now()) {
      if (valid) return machine
      throw failure.error
    }
    if (valid && (machine.accessTokenExpiresAt ?? 0) * 1000 - Date.now() > 120_000) return machine

    clearTimer(name)
    const controller = new AbortController()
    const stop = () => controller.abort(lifetime.signal.reason)
    lifetime.signal.addEventListener("abort", stop, { once: true })
    const deadline = setTimeout(() => controller.abort(new DOMException("Machine access renewal timed out", "TimeoutError")), 10_000)
    const request = Promise.resolve()
      .then(() => {
        if (disposed) throw new ServerUnavailableError()
        return abortable(options.refresh(machine, controller.signal), controller.signal)
      })
      .then((refreshed) => {
        const latest = options.read(name)
        if (disposed || !latest) throw new ServerUnavailableError()
        if (!refreshed.accessToken || refreshed.accessToken === rejected.get(name) || (refreshed.accessTokenExpiresAt ?? 0) * 1000 <= Date.now()) {
          throw new ServerUnavailableError()
        }
        failures.delete(name)
        const next = {
          ...latest,
          auth: undefined,
          accessToken: refreshed.accessToken,
          accessTokenExpiresAt: refreshed.accessTokenExpiresAt,
          accessError: undefined,
        }
        options.update(next)
        return next
      })
      .catch((error) => {
        const latest = options.read(name)
        if (!disposed && latest) {
          const previous = failures.get(name)
          const denied = !!previous?.denied || !transientMachineAccessFailure(error)
          const count = (previous?.count ?? 0) + 1
          failures.set(name, { error, retryAt: Date.now() + accessRetryDelay(count, denied), denied, count })
          options.failed(name, error, usable(latest))
        }
        throw error
      })
      .finally(() => {
        clearTimeout(deadline)
        lifetime.signal.removeEventListener("abort", stop)
        pending.delete(name)
        schedule(name)
      })
    pending.set(name, request)
    if (valid) {
      // Renewal is desirable, but does not shorten the credential's authority.
      void request.catch(() => {})
      return machine
    }
    return request
  }

  const refresh = async (name: string) => {
    if (disposed || !visible || !kept.includes(name)) return
    try {
      await ensure(name)
    } catch {
      // ensure publishes the failure; background renewal retries on its timer.
    } finally {
      schedule(name)
    }
  }

  /**
   * Keeps `names` renewed ahead of expiry while the page is visible. A machine
   * newly kept, or every one when the page is shown again, renews at once if
   * it needs to; the rest keep their schedule.
   */
  const sync = (names: readonly string[], pageVisible: boolean) => {
    if (disposed) return
    const shown = pageVisible && !visible
    const previous = kept
    kept = names
    visible = pageVisible
    for (const name of [...timers.keys()]) {
      if (!visible || !kept.includes(name)) clearTimer(name)
    }
    if (!visible) return
    for (const name of kept) {
      if (shown || !previous.includes(name)) void refresh(name)
      else schedule(name)
    }
  }

  const dispose = () => {
    disposed = true
    for (const name of [...timers.keys()]) clearTimer(name)
    lifetime.abort()
    pending.clear()
    failures.clear()
    rejected.clear()
  }

  /**
   * Takes a grant issued alongside another machine's renewal. A pending renewal,
   * a rejected or denied grant, or a later expiry already held wins.
   */
  const adopt = (name: string, grant: { token: string; expiresAt: number }) => {
    if (disposed || pending.has(name) || failures.get(name)?.denied) return
    const machine = options.read(name)
    if (!machine || grant.token === rejected.get(name) || (machine.accessTokenExpiresAt ?? 0) >= grant.expiresAt) return
    failures.delete(name)
    options.update({ ...machine, auth: undefined, accessToken: grant.token, accessTokenExpiresAt: grant.expiresAt, accessError: undefined })
  }

  /**
   * The network may have changed: it came back, the page was shown again, or
   * the user asked to retry. Network failures from before say nothing about it
   * now, so the next request renews at once. Denials keep their wait.
   */
  const forgetNetworkFailures = () => {
    for (const [name, failure] of failures) {
      if (!failure.denied) failures.delete(name)
    }
  }

  return { ensure, sync, adopt, forgetNetworkFailures, dispose }
}
